import { LinearGradient } from "expo-linear-gradient";
import { Image as ExpoImage } from "expo-image";
import { useRef, useState } from "react";
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  View,
  type ImageStyle,
  type StyleProp,
  type ViewStyle
} from "react-native";

import { cardSaturation } from "@/design/backdropTone";
import { brandBackdropSource } from "@/design/brandBackdrop";
import { Text } from "@/design/Text";
import { fonts, palette } from "@/design/tokens";
import {
  getDecodedImageUri,
  hasDecodedImage,
  markImageDecoded
} from "@/lib/imageReadiness";
import { useAppComposed, useReadinessHold } from "@/providers/ReadinessGate";

/**
 * Recital artwork system. Every recital gets a visual identity from its
 * program-cover art (operator-designed, one per year/show). When no cover
 * exists yet we fall back to a quiet branded gradient poster — never a gray
 * box.
 *
 * `Backdrop` is the signature technique: the same artwork, blurred and
 * dimmed, becomes the background of the card/hero that contains it —
 * sharp image over its own soft glow.
 *
 * New images ease in on load rather than popping. Prefetched or previously
 * painted images render at their settled opacity immediately, so history
 * restoration never replays a placeholder transition.
 */

const useNative = Platform.OS !== "web";

/**
 * RN's `filter` style takes an array of operation objects on native and a CSS
 * string through react-native-web. Both landed in RN 0.76, so this needs no new
 * native module. It lives here rather than beside the tone constants because
 * those are imported by webPersistentBackdrop.ts, which is unit-tested in plain
 * Node and must stay free of `react-native` imports.
 */
function saturateStyle(amount: number): ImageStyle {
  return (
    Platform.OS === "web"
      ? { filter: `saturate(${amount})` }
      : { filter: [{ saturate: amount }] }
  ) as unknown as ImageStyle;
}

function afterPaint(callback: () => void): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
}

const fallbackGradients: [string, string][] = [
  ["#274468", "#131A2C"],
  ["#33285C", "#151228"],
  ["#4A2B50", "#181121"],
  ["#1F4A55", "#0F1B22"],
  ["#41304F", "#141020"]
];

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function fallbackFor(id: string): [string, string] {
  return fallbackGradients[hashString(id) % fallbackGradients.length];
}

function ArtworkFallback({
  fallbackSeed,
  label
}: {
  fallbackSeed: string;
  label?: string;
}) {
  const [top, bottom] = fallbackFor(fallbackSeed);
  const [labelSize, setLabelSize] = useState(28);
  return (
    <>
      <LinearGradient colors={[top, bottom]} style={StyleSheet.absoluteFill} />
      {label ? (
        <View
          style={styles.fallbackCenter}
          onLayout={(event) => {
            const { height, width } = event.nativeEvent.layout;
            setLabelSize(Math.max(12, Math.min(width * 0.28, height * 0.3)));
          }}
        >
          <Text
            style={[
              styles.fallbackLabel,
              { fontSize: labelSize, lineHeight: labelSize * 1.1 }
            ]}
          >
            {label}
          </Text>
        </View>
      ) : null}
    </>
  );
}

function useDecodedImageReveal(uri: string | null, duration: number, readinessKey: string) {
  // Successful preparation and previously displayed images share the same
  // registry/cache as Artwork. Never replay a reveal for either case.
  const decodedOnMount = useRef(uri ? hasDecodedImage(uri) : false).current;
  const fade = useRef(new Animated.Value(decodedOnMount ? 1 : 0)).current;
  const started = useRef(false);
  const [settled, setSettled] = useState(decodedOnMount || !uri);
  const appComposed = useAppComposed();
  useReadinessHold(`${readinessKey}:${uri ?? "none"}`, settled);

  const onLoad = () => {
    if (!uri) return;
    markImageDecoded(uri);
    if (started.current) return;
    started.current = true;
    if (decodedOnMount || !appComposed) {
      fade.setValue(1);
      // React Native's load event means the decoded source is available, not
      // that the updated pixels have necessarily reached the screen. Keep the
      // curtain hold through two presentation frames so readiness tracks a
      // painted image rather than a completed request.
      afterPaint(() => setSettled(true));
      return;
    }
    Animated.timing(fade, {
      toValue: 1,
      duration,
      easing: Easing.out(Easing.quad),
      useNativeDriver: useNative
    }).start(() => setSettled(true));
  };

  const onError = () => {
    // A branded fallback is a complete visual state. Settle the readiness
    // hold only after the image component has reported the failure so the
    // fallback commits underneath the curtain before its debounce expires.
    setSettled(true);
  };

  return { decodedOnMount, fade, onError, onLoad, settled };
}

function ArtworkImageLayer({
  uri,
  placeholderUri,
  fallbackSeed,
  label
}: {
  uri: string;
  placeholderUri?: string | null;
  fallbackSeed: string;
  label?: string;
}) {
  const [failed, setFailed] = useState(false);
  const reveal = useDecodedImageReveal(uri, 220, `artwork:${fallbackSeed}`);
  const { decodedOnMount, fade, onLoad, settled } = reveal;
  // A cache miss may mount the generated poster beneath Expo Image while the
  // real pixels decode. Once those pixels have actually been displayed, the
  // fallback is no longer part of the tile's visual state and must unmount;
  // leaving it underneath forever lets native pressed/focus compositing expose
  // stale labels through an otherwise opaque cover.
  const [displayed, setDisplayed] = useState(decodedOnMount);

  const handleDisplay = () => {
    setDisplayed(true);
    onLoad();
  };

  if (failed) return <ArtworkFallback fallbackSeed={fallbackSeed} label={label} />;
  if (useNative) {
    return (
      <>
        {!displayed && !placeholderUri ? (
          <ArtworkFallback fallbackSeed={fallbackSeed} label={label} />
        ) : null}
        <ExpoImage
          source={uri}
          placeholder={!decodedOnMount ? placeholderUri : null}
          contentFit="cover"
          placeholderContentFit="cover"
          cachePolicy="memory-disk"
          priority="high"
          transition={decodedOnMount ? 0 : 220}
          style={StyleSheet.absoluteFill}
          onDisplay={handleDisplay}
          onError={() => {
            reveal.onError();
            setFailed(true);
          }}
        />
      </>
    );
  }
  return (
    <>
      {!settled && !placeholderUri ? (
        <ArtworkFallback fallbackSeed={fallbackSeed} label={label} />
      ) : null}
      {placeholderUri && !settled ? (
        <Animated.Image
          source={{ uri: placeholderUri }}
          style={StyleSheet.absoluteFill}
          blurRadius={4}
        />
      ) : null}
      <Animated.Image
        source={{ uri }}
        resizeMode="cover"
        fadeDuration={0}
        style={[StyleSheet.absoluteFill, { opacity: fade }]}
        onLoad={onLoad}
        onError={() => {
          reveal.onError();
          setFailed(true);
        }}
      />
    </>
  );
}

function BackdropLayer({
  uri,
  fallbackSeed,
  intensity,
  saturateArtwork
}: {
  uri: string | null;
  fallbackSeed: string;
  intensity: number;
  saturateArtwork: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const [top, bottom] = fallbackFor(fallbackSeed);
  const reveal = useDecodedImageReveal(uri, 320, `backdrop:${fallbackSeed}`);
  const fade = reveal.fade;
  const saturate = saturateArtwork ? saturateStyle(cardSaturation()) : null;

  if (useNative) {
    return (
      <>
        <Animated.View
          style={[
            StyleSheet.absoluteFill,
            {
              opacity: Animated.multiply(
                Animated.subtract(1, fade),
                0.75 * intensity
              )
            }
          ]}
        >
          <LinearGradient
            colors={[top, bottom]}
            style={StyleSheet.absoluteFill}
          />
        </Animated.View>
        {uri && !failed ? (
          <ExpoImage
            source={brandBackdropSource(uri)}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={reveal.decodedOnMount ? 0 : 320}
            blurRadius={42}
            onDisplay={reveal.onLoad}
            onError={() => {
              reveal.onError();
              setFailed(true);
            }}
            style={[styles.backdropImage, saturate, { opacity: 0.5 * intensity }]}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <Animated.View
        style={[
          StyleSheet.absoluteFill,
          { opacity: Animated.multiply(Animated.subtract(1, fade), 0.75 * intensity) }
        ]}
      >
        <LinearGradient colors={[top, bottom]} style={StyleSheet.absoluteFill} />
      </Animated.View>
      {uri && !failed ? (
        <Animated.Image
          source={brandBackdropSource(uri)}
          resizeMode="cover"
          blurRadius={42}
          fadeDuration={0}
          onLoad={reveal.onLoad}
          onError={() => {
            reveal.onError();
            reveal.fade.setValue(0);
            setFailed(true);
          }}
          style={[
            styles.backdropImage,
            saturate,
            { opacity: Animated.multiply(fade, 0.5 * intensity) }
          ]}
        />
      ) : null}
    </>
  );
}

/** Sharp artwork tile (poster or thumb). */
export function Artwork({
  uri,
  placeholderUri,
  fallbackSeed,
  label,
  style
}: {
  uri: string | null;
  /** Tiny inline blur placeholder (data URL) shown while loading. */
  placeholderUri?: string | null;
  fallbackSeed: string;
  /** Short text drawn on the generated fallback (e.g. "R1"). */
  label?: string;
  style?: StyleProp<ViewStyle>;
}) {
  const renderUri = uri
    ? getDecodedImageUri(uri) ?? uri
    : null;
  return (
    <View style={[styles.tile, style]}>
      {renderUri ? (
        <ArtworkImageLayer
          key={renderUri}
          uri={renderUri}
          placeholderUri={placeholderUri}
          fallbackSeed={fallbackSeed}
          label={label}
        />
      ) : (
        <ArtworkFallback fallbackSeed={fallbackSeed} label={label} />
      )}
    </View>
  );
}

/**
 * Blurred artwork backdrop for the surface that contains the sharp copy.
 * Scaled up slightly so blur edges never show.
 */
export function Backdrop({
  uri,
  fallbackSeed,
  intensity = 1,
  saturateArtwork = false
}: {
  uri: string | null;
  fallbackSeed: string;
  /** 0..1 — how much the artwork shows through. */
  intensity?: number;
  /**
   * Restore chroma the card's scrim destroys — see design/backdropTone.ts.
   *
   * OPT-IN, and it must stay that way. It exists for surfaces glowing with
   * *stage stills*, which this card's scrim flattens to brown. Surfaces glowing
   * with the branded background art (home) or with video frames (player veil,
   * displaced/swap panels) are already correct and must not be touched — Dax,
   * 2026-07-29, after an unconditional version altered the home cards.
   */
  saturateArtwork?: boolean;
}) {
  const renderUri = uri
    ? getDecodedImageUri(uri) ?? uri
    : null;
  return (
    <View style={[styles.backdrop, { pointerEvents: "none" }]}>
      {/* The gradient underlays until the image lands, then crossfades away
          — a late image eases over a composed surface, never a hole, and the
          settled look matches the plain-image rendering exactly. */}
      <BackdropLayer
        key={renderUri ?? `fallback:${fallbackSeed}`}
        uri={renderUri}
        fallbackSeed={fallbackSeed}
        intensity={intensity}
        saturateArtwork={saturateArtwork}
      />
      {/* Settle the image into the canvas */}
      <LinearGradient
        colors={["rgba(13, 14, 20, 0.25)", "rgba(13, 14, 20, 0.92)"]}
        style={StyleSheet.absoluteFill}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  tile: {
    backgroundColor: palette.surfaceSunken,
    overflow: "hidden"
  },
  fallbackCenter: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center"
  },
  fallbackLabel: {
    color: "rgba(243, 244, 250, 0.55)",
    fontFamily: fonts.display,
    letterSpacing: -0.5
  },
  backdrop: {
    bottom: 0,
    left: 0,
    overflow: "hidden",
    position: "absolute",
    right: 0,
    top: 0
  },
  backdropImage: {
    height: "120%",
    left: "-10%",
    position: "absolute",
    top: "-10%",
    width: "120%"
  }
});
